/**
 * SSE subscriber — streams `/api/v2/sync/stream` over a native `fetch` +
 * `ReadableStream` transport and routes inbound events through
 * `engine.applyOne` (T12.0.17 Phase 2; transport swapped in T12.0.23).
 *
 * Transport rules (T12.0.23):
 *   - The Bearer token rides in the `Authorization` header, NOT the URL
 *     (closes the OWASP CWE-598 token-in-URL exposure). `deviceId` stays a
 *     query param — it is not a secret; the server registry keys on it for
 *     own-device exclusion (T12.0.17 D4/D6).
 *   - `credentials: 'include'` keeps the legacy refresh-token cookie on the
 *     request across the BACKEND_HOST / FRONTEND_HOST split.
 *   - `EventSource` can't set headers and is GET-only, so we use native
 *     `fetch` and parse SSE frames in-tree (D1 — no library dependency).
 *
 * Reconnect (T12.0.23 D4):
 *   - We own the loop. `fetch` has no auto-reconnect, so there's no stale-URL
 *     re-send to fight. On stream end / error we `await refresh()` then
 *     reconnect with exponential backoff + jitter; the backoff resets on a
 *     successful open. `AbortController` drives `dispose()` / `reopen()`.
 *   - `await refresh()` before every (re)connect refreshes the access token
 *     through the same deduped path the REST client uses (D3), so the 15-min
 *     access-token TTL no longer produces a permanent 401 loop.
 *
 * Visibility (T12.0.17 D3 preserved):
 *   - On `visibilitychange` → visible: if the stream isn't healthy, abort and
 *     reconnect immediately. Fire `onTabReturn` so the caller can kick a pull
 *     regardless of connection state.
 *
 * Reporting (T12.0.23 D5):
 *   - A transient blip (drop → quick reconnect) produces NO Sentry error.
 *     Only a sustained outage — the reconnect loop failing past a threshold —
 *     reports, and it reports exactly once until the next successful open
 *     resets the gate. Folds in the SHOPISTA-FRONT-1/2 over-reporting fix.
 *
 * Payload format: backend emits one `data: <json>\n\n` per event where the
 * JSON body is a `ServerEventV2`. Heartbeats are `:\n\n` comment frames and
 * parse as no-ops; `id:` lines are ignored (no `Last-Event-ID` replay).
 */

import type {
  ServerEventV2,
  SyncEngine,
  SyncIdentity,
} from '@shopista/shared/sync';
import type { TokenProvider } from '@shopista/shared/api';
// T12.0.22 — error branches additionally report to Sentry when initialized.
// The import is safe with or without `VITE_SENTRY_DSN`; calls on an
// uninitialized SDK are no-ops.
import { Sentry } from '../sentry';

export type SseConnectionState = 'connecting' | 'open' | 'disconnected';

/**
 * Minimal typed shape of the `fetch` the subscriber needs. Declared locally
 * so tests can inject a fake returning a `ReadableStream` body without
 * pulling the full DOM `fetch` overload set into play.
 */
export type SseFetch = (
  input: string,
  init: {
    method?: string;
    headers?: Record<string, string>;
    credentials?: 'include' | 'omit' | 'same-origin';
    signal?: AbortSignal;
  }
) => Promise<SseFetchResponse>;

export interface SseFetchResponse {
  ok: boolean;
  status: number;
  body: ReadableStream<Uint8Array> | null;
}

export interface AttachSseSubscriberOptions {
  /** Backend base URL. */
  apiBaseUrl: string;
  /** Token provider — Bearer token read on every (re)connect. */
  tokens: TokenProvider;
  /**
   * Shared refresh handle (T12.0.23 D3). Awaited before every (re)connect so
   * a soft-expired access token is refreshed through the deduped path the
   * REST client uses. Returns the new token, or `null` on refresh failure.
   */
  refresh: () => Promise<string | null>;
  /** Engine — `applyOne` runs for every inbound event. */
  engine: SyncEngine;
  /**
   * Identity resolver — used to send `deviceId` on connect (the backend
   * subscriber registry uses it for own-device exclusion, D4).
   */
  identity: () => SyncIdentity | null;
  /**
   * Fired on tab-return regardless of connection state — caller hooks this
   * to fire an immediate `engine.pull()` per D3.
   */
  onTabReturn?: () => void;
  /** State-change hook for the indicator (D5). */
  onStateChange?: (state: SseConnectionState) => void;
  /**
   * Optional logger. Defaults to `console.error` so error-branch drift
   * (T12.0.17 Round 4 silent-catch) is visible — payload shape mismatches
   * surface immediately. Tests that assert silence inject `() => {}`.
   */
  logger?: (msg: string, meta?: unknown) => void;
  /**
   * Optional `fetch` injection — tests inject a fake returning a
   * `ReadableStream`. Defaults to the browser global.
   */
  fetchImpl?: SseFetch;
  /**
   * Visibility readout / target — tests inject fakes; production uses
   * `document`.
   */
  isVisible?: () => boolean;
  visibilityTarget?: {
    addEventListener: (type: string, listener: () => void) => void;
    removeEventListener: (type: string, listener: () => void) => void;
  };
  /**
   * Backoff tuning — defaults below. Tests override for deterministic timing.
   */
  backoff?: {
    /** First retry delay in ms (default 1000). */
    baseMs?: number;
    /** Cap on the delay in ms (default 30_000). */
    maxMs?: number;
    /**
     * Consecutive failures before a sustained-outage Sentry report fires
     * (default 4). Below this, drops are treated as transient blips and stay
     * silent (D5).
     */
    sustainedFailureThreshold?: number;
  };
  /**
   * Schedule a delayed callback. Defaults to `setTimeout`/`clearTimeout`;
   * tests inject fake timers via vitest's `vi.useFakeTimers()` which patch
   * the globals, so the default is already test-friendly. Exposed for clarity.
   */
  scheduler?: {
    setTimeout: (cb: () => void, ms: number) => ReturnType<typeof setTimeout>;
    clearTimeout: (handle: ReturnType<typeof setTimeout>) => void;
  };
}

export interface SseSubscriberHandle {
  /** Current connection state (synchronous mirror of the last emit). */
  getState: () => SseConnectionState;
  /** Force-close and re-open. Test hook + tab-return reconnect path. */
  reopen: () => void;
  /** Dispose — aborts the stream, removes listeners. */
  dispose: () => void;
}

const DEFAULT_BASE_MS = 1_000;
const DEFAULT_MAX_MS = 30_000;
const DEFAULT_SUSTAINED_THRESHOLD = 4;

/**
 * Open + manage an SSE subscriber. Returns a handle for shutdown.
 */
export function attachSseSubscriber(
  opts: AttachSseSubscriberOptions
): SseSubscriberHandle {
  const log =
    opts.logger ??
    ((msg, meta) => {
      // Default: emit to console so error branches don't go silent. The
      // T12.0.17 Round 4 root cause (camelCase ↔ snake_case payload drift
      // throwing inside engine.applyOne) was masked for three review
      // rounds by a no-op default logger. Tests that assert silence
      // inject an explicit `() => {}`.
      if (meta === undefined) console.error('[sse]', msg);
      else console.error('[sse]', msg, meta);
    });
  const fetchImpl =
    opts.fetchImpl ??
    ((input, init) =>
      (globalThis.fetch as unknown as SseFetch)(input, init));
  const isVisible =
    opts.isVisible ??
    (() =>
      typeof document !== 'undefined'
        ? document.visibilityState === 'visible'
        : true);
  const visibilityTarget =
    opts.visibilityTarget ??
    (typeof document !== 'undefined'
      ? document
      : {
          addEventListener: () => {},
          removeEventListener: () => {},
        });
  const scheduler = opts.scheduler ?? {
    setTimeout: (cb, ms) => setTimeout(cb, ms),
    clearTimeout: (handle) => clearTimeout(handle),
  };

  const baseMs = opts.backoff?.baseMs ?? DEFAULT_BASE_MS;
  const maxMs = opts.backoff?.maxMs ?? DEFAULT_MAX_MS;
  const sustainedThreshold =
    opts.backoff?.sustainedFailureThreshold ?? DEFAULT_SUSTAINED_THRESHOLD;

  let disposed = false;
  let state: SseConnectionState = 'disconnected';
  // The controller for the in-flight (or pending) stream. Aborting it tears
  // down the current `fetch` + reader and signals the run loop to stop.
  let controller: AbortController | null = null;
  // Identifies the active run loop so a stale loop (after reopen/dispose)
  // exits instead of scheduling more reconnects.
  let generation = 0;
  // Pending backoff timer handle so dispose can cancel a scheduled reconnect.
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  // Consecutive connect failures — drives backoff growth + the D5 threshold.
  let consecutiveFailures = 0;
  // D5 gate: true once a sustained-outage report has fired; reset on open so
  // the next sustained outage reports again, but a single outage reports once.
  let sustainedReported = false;

  const setState = (next: SseConnectionState): void => {
    if (state === next) return;
    state = next;
    opts.onStateChange?.(state);
  };

  /**
   * Backoff delay for the Nth consecutive failure: exponential growth capped
   * at `maxMs`, plus up to ±50% jitter so reconnects from many tabs don't
   * synchronize. `failureIndex` is 1-based (first retry = baseMs-ish).
   */
  const backoffDelay = (failureIndex: number): number => {
    const exp = Math.min(maxMs, baseMs * 2 ** (failureIndex - 1));
    const jitter = exp * 0.5 * (Math.random() * 2 - 1);
    return Math.max(0, Math.round(exp + jitter));
  };

  /**
   * One end-to-end connect attempt: refresh the token, open the stream, read
   * + parse frames until the stream ends or errors. Returns when the stream
   * is done (clean end or thrown error); never schedules its own reconnect —
   * the run loop owns scheduling.
   */
  const connectOnce = async (myGeneration: number): Promise<void> => {
    if (disposed || myGeneration !== generation) return;
    const identity = opts.identity();
    if (!identity) {
      // No identity (mid-logout / hydrating) — leave closed, do not retry.
      setState('disconnected');
      throw new StopLoop();
    }

    setState('connecting');

    // D3 — refresh the access token through the shared deduped path before
    // each (re)connect. On refresh failure `refresh()` returns null and has
    // already cleared tokens + dispatched session-cleared; fall back to a
    // direct read in case another path refreshed concurrently.
    let token: string | null = null;
    try {
      token = await opts.refresh();
    } catch (err) {
      log('sse: refresh threw', { error: describe(err) });
    }
    if (!token) token = opts.tokens.getAccessToken();
    if (disposed || myGeneration !== generation) return;
    if (!token) {
      // No token at all — the user is mid-logout. Stop the loop; the engine
      // lifecycle is also gated on auth.
      setState('disconnected');
      throw new StopLoop();
    }

    const params = new URLSearchParams({ deviceId: identity.deviceId });
    const url = `${opts.apiBaseUrl}/api/v2/sync/stream?${params.toString()}`;

    const localController = new AbortController();
    controller = localController;

    let response: SseFetchResponse;
    try {
      response = await fetchImpl(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'text/event-stream',
        },
        credentials: 'include',
        signal: localController.signal,
      });
    } catch (err) {
      if (disposed || myGeneration !== generation) return;
      // Network-level failure (offline, DNS, abort). Treated as a drop — the
      // run loop schedules a backoff retry.
      throw new ConnectError(`fetch threw: ${describe(err)}`);
    }

    if (disposed || myGeneration !== generation) return;

    if (!response.ok || response.body === null) {
      // Non-2xx (401 past TTL, 5xx) or no body. The next loop iteration runs
      // `refresh()` again, so a 401 recovers with a fresh token without a
      // separate retry branch here.
      throw new ConnectError(`stream HTTP ${response.status}`);
    }

    // First bytes / open: stream accepted. Reset the failure gate.
    consecutiveFailures = 0;
    sustainedReported = false;
    setState('open');
    log('sse: open');

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        if (disposed || myGeneration !== generation) {
          // Torn down mid-stream — stop reading and let the loop exit.
          try {
            await reader.cancel();
          } catch {
            // ignore
          }
          return;
        }
        buffer += decoder.decode(value, { stream: true });
        // SSE frames are separated by a blank line (`\n\n`). Keep the
        // trailing partial frame in the buffer.
        let sep = buffer.indexOf('\n\n');
        while (sep !== -1) {
          const frame = buffer.slice(0, sep);
          buffer = buffer.slice(sep + 2);
          await handleFrame(frame);
          sep = buffer.indexOf('\n\n');
        }
      }
    } catch (err) {
      if (disposed || myGeneration !== generation) return;
      throw new ConnectError(`stream read failed: ${describe(err)}`);
    }

    // Clean stream end (server closed). Surface as a drop so the loop
    // reconnects — but this is not a connect FAILURE, so don't grow backoff
    // from it as if the token were bad.
    if (disposed || myGeneration !== generation) return;
    throw new StreamEnded();
  };

  /**
   * Parse one SSE frame and route `data:` payloads through `engine.applyOne`.
   * `:` comment frames (heartbeats) and `id:` lines are no-ops (T12.0.17 D2;
   * no `Last-Event-ID` replay).
   */
  const handleFrame = async (frame: string): Promise<void> => {
    if (disposed) return;
    const dataLines: string[] = [];
    for (const rawLine of frame.split('\n')) {
      const line = rawLine.replace(/\r$/, '');
      if (line === '' || line.startsWith(':')) continue; // heartbeat / blank
      if (line.startsWith('id:')) continue; // no replay cursor
      if (line.startsWith('data:')) {
        // Per the SSE spec a single leading space after the colon is stripped.
        dataLines.push(line.slice(5).replace(/^ /, ''));
        continue;
      }
      // Unknown field (e.g. `event:`) — ignore; the backend only emits data.
    }
    if (dataLines.length === 0) return; // comment-only / heartbeat frame
    const data = dataLines.join('\n');

    let parsed: ServerEventV2;
    try {
      parsed = JSON.parse(data) as ServerEventV2;
    } catch (err) {
      log('sse: malformed payload', { error: describe(err) });
      reportToSentry(err, 'malformed_payload');
      return;
    }
    try {
      await opts.engine.applyOne(parsed);
    } catch (err) {
      log('sse: applyOne failed', { error: describe(err) });
      reportToSentry(err, 'apply_one_failed');
    }
  };

  /**
   * The managed reconnect loop. Runs `connectOnce`; on a drop, counts the
   * failure, applies D5 reporting, and schedules a backoff retry — unless the
   * loop was superseded (reopen/dispose) or explicitly stopped.
   */
  const runLoop = (myGeneration: number): void => {
    void (async () => {
      try {
        await connectOnce(myGeneration);
      } catch (err) {
        if (disposed || myGeneration !== generation) return;
        if (err instanceof StopLoop) {
          // Intentional stop (no identity / token) — don't reconnect.
          return;
        }
        // A drop. `StreamEnded` (clean server close) reconnects promptly
        // without inflating the failure count; `ConnectError` grows backoff
        // and feeds the D5 sustained-outage gate.
        setState('disconnected');
        if (err instanceof ConnectError) {
          consecutiveFailures += 1;
          log('sse: connect failed', {
            attempt: consecutiveFailures,
            error: describe(err),
          });
          // D5 — only a sustained outage reports, and only once per outage.
          if (
            consecutiveFailures >= sustainedThreshold &&
            !sustainedReported
          ) {
            sustainedReported = true;
            reportToSentry(
              new Error(
                `sse: sustained outage (${consecutiveFailures} consecutive failures)`
              ),
              'sustained_outage'
            );
          }
        } else {
          log('sse: stream ended', { error: describe(err) });
        }

        // Schedule the reconnect. `StreamEnded` retries near-immediately;
        // `ConnectError` backs off exponentially.
        const delay =
          err instanceof ConnectError
            ? backoffDelay(consecutiveFailures)
            : baseMs;
        scheduleReconnect(myGeneration, delay);
        return;
      }
      // connectOnce returned without throwing only when the loop was torn
      // down mid-flight (disposed / superseded) — nothing to schedule.
    })();
  };

  const scheduleReconnect = (myGeneration: number, delay: number): void => {
    if (disposed || myGeneration !== generation) return;
    reconnectTimer = scheduler.setTimeout(() => {
      reconnectTimer = null;
      if (disposed || myGeneration !== generation) return;
      runLoop(myGeneration);
    }, delay);
  };

  const abortCurrent = (): void => {
    if (reconnectTimer !== null) {
      scheduler.clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    if (controller !== null) {
      try {
        controller.abort();
      } catch {
        // ignore
      }
      controller = null;
    }
  };

  /**
   * Start a fresh run loop, abandoning any in-flight stream / pending retry.
   */
  const start = (): void => {
    if (disposed) return;
    abortCurrent();
    generation += 1;
    consecutiveFailures = 0;
    sustainedReported = false;
    runLoop(generation);
  };

  const reopen = (): void => {
    start();
  };

  const onVisibilityChange = (): void => {
    if (disposed) return;
    if (!isVisible()) return;
    // D3 — on tab-return: if the stream isn't healthy, reconnect now instead
    // of waiting out the backoff. Either way fire `onTabReturn` so the caller
    // can kick a pull.
    if (state !== 'open') {
      log('sse: tab-return reopen', { state });
      start();
    }
    opts.onTabReturn?.();
  };

  visibilityTarget.addEventListener('visibilitychange', onVisibilityChange);

  if (isVisible()) {
    start();
  }

  return {
    getState: () => state,
    reopen,
    dispose() {
      disposed = true;
      visibilityTarget.removeEventListener(
        'visibilitychange',
        onVisibilityChange
      );
      abortCurrent();
      setState('disconnected');
    },
  };
}

/** Sentinel: stop the loop intentionally (no identity / no token). */
class StopLoop extends Error {
  constructor() {
    super('sse: stop loop');
    this.name = 'StopLoop';
  }
}

/** Sentinel: a connect-level failure (bad response / fetch threw / read err). */
class ConnectError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ConnectError';
  }
}

/** Sentinel: the server cleanly closed the stream (reconnect promptly). */
class StreamEnded extends Error {
  constructor() {
    super('sse: stream ended');
    this.name = 'StreamEnded';
  }
}

function describe(err: unknown): string {
  if (err instanceof Error) return `${err.name}: ${err.message}`;
  return String(err);
}

/**
 * Report an SSE error branch to Sentry, tagged with the branch name so
 * the issue grouping is grain-stable. Safe no-op when Sentry was never
 * initialized (i.e. `VITE_SENTRY_DSN` unset — local dev) — `captureException`
 * on an uninitialized SDK client does nothing.
 */
function reportToSentry(err: unknown, branch: string): void {
  Sentry.captureException(err, { extra: { sse_event: branch } });
}
